import React, { useEffect, useState } from 'react';
import { api, API } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { FileText, Download, Mail, FileSpreadsheet, CreditCard, Lock } from 'lucide-react';

const riskClass = { low: 'pill-low', medium: 'pill-medium', high: 'pill-high', critical: 'pill-critical' };

const Reports = () => {
  const { user } = useAuth();
  const isPaid = user?.payment_status === 'paid' || user?.role === 'admin';
  const [scans, setScans] = useState([]);
  useEffect(() => { api.get('/scans').then((r) => setScans(r.data)); }, []);

  const emailReport = async (scan_id) => {
    if (!isPaid) { toast.error('Payment required — admin must mark account as paid first.'); return; }
    try {
      await api.post('/reports/email', { scan_id });
      toast.success('Report queued — delivery is MOCKED in demo (logged to DB)');
    } catch (_e) { toast.error('Could not queue email'); }
  };

  const guardedDownload = (e) => {
    if (!isPaid) {
      e.preventDefault();
      toast.error('Payment required — admin must mark account as paid first.');
    }
  };

  return (
    <div className="space-y-8">
      <header>
        <div className="label-tag mb-2">// Reports</div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tight">Security reports</h1>
        <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
          Export forensic-grade reports per scan. Monthly summary roll-ups available for Web PI tier.
        </p>
      </header>

      {!isPaid && (
        <div data-testid="payment-required-banner-reports" className="panel p-6 border-[color:var(--status-medium)] bg-[rgba(255,179,2,0.06)] flex items-start gap-4">
          <Lock className="text-[color:var(--status-medium)] mt-1" size={20} />
          <div>
            <div className="label-tag text-[color:var(--status-medium)] mb-1">// Downloads Locked</div>
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
              PDF and CSV downloads are unavailable until an admin marks your account as <span className="font-mono text-[color:var(--status-low)]">PAID</span>.
              You can browse the scan list below; export buttons will activate the moment payment is confirmed.
            </p>
          </div>
        </div>
      )}
      <div data-testid={APP.reportsList} className="panel overflow-x-auto">
        <table className="dtable min-w-[900px]">
          <thead><tr><th>Scan</th><th>Network</th><th>Devices</th><th>New</th><th>Risk</th><th>Date</th><th className="text-right">Actions</th></tr></thead>
          <tbody>
            {scans.length === 0 ? (
              <tr><td colSpan={7} className="text-[color:var(--text-secondary)]">No scans yet. Generate one from Network Scan.</td></tr>
            ) : scans.map((s) => (
              <tr key={s.scan_id}>
                <td className="text-[color:var(--brand)]">{s.scan_id.slice(-10)}</td>
                <td>{s.network_name}</td>
                <td>{s.devices_found}</td>
                <td>{s.new_devices}</td>
                <td><span className={`pill ${riskClass[s.risk_level]}`}>{s.risk_level}</span></td>
                <td className="text-[color:var(--text-secondary)]">{new Date(s.started_at).toLocaleString()}</td>
                <td className="text-right">
                  <div className="inline-flex gap-2">
                    <a data-testid={APP.reportPdfBtn} href={`${API}/scans/${s.scan_id}/pdf`} onClick={guardedDownload} target="_blank" rel="noreferrer" className={`btn-secondary !py-2 !text-[10px] ${!isPaid ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      {isPaid ? <Download size={11} /> : <Lock size={11} />} PDF
                    </a>
                    <a data-testid={APP.reportCsvBtn} href={`${API}/scans/${s.scan_id}/csv`} onClick={guardedDownload} target="_blank" rel="noreferrer" className={`btn-secondary !py-2 !text-[10px] ${!isPaid ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      {isPaid ? <FileSpreadsheet size={11} /> : <Lock size={11} />} CSV
                    </a>
                    <button data-testid={APP.reportEmailBtn} onClick={() => emailReport(s.scan_id)} disabled={!isPaid} className={`btn-secondary !py-2 !text-[10px] ${!isPaid ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      {isPaid ? <Mail size={11} /> : <Lock size={11} />} Email
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="panel p-6 flex items-start gap-4">
        <FileText className="text-[color:var(--brand)] mt-1" size={20} />
        <div className="text-sm text-[color:var(--text-secondary)]">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">// Note</span><br/>
          Email and SMS delivery channels are wired but <strong>MOCKED</strong> in this demo: messages are logged to MongoDB. Add <code className="text-white">RESEND_API_KEY</code> / Twilio creds in <code className="text-white">backend/.env</code> to enable live delivery.
        </div>
      </div>
    </div>
  );
};

export default Reports;
